/**
 * Auth context store for the Eyrie UI.
 *
 * - Persists access + refresh tokens and the current principal profile in
 *   localStorage under "eyrie.auth".
 * - Wires the SDK's request interceptor so every SDK call carries the bearer
 *   token automatically.
 * - Auto-refreshes the access token ~60s before expiry by decoding the JWT
 *   `exp` claim client-side; if refresh fails the session is cleared.
 *
 * Two principal types coexist on the same JWT pipeline:
 *   - "user" — a customer (per-org User).
 *   - "account" — a platform admin.
 * The `scope` field of `MeProfile` is what we use to gate /admin/* routes.
 */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Side-effect: configure SDK base URL.
import "@/lib/sdk";

import {
  type LoginInputBodyWritable,
  type MeProfile,
  type TokenBundle,
  authAdminLogin,
  authLogin,
  authLogout,
  authMe,
  authRefresh,
} from "@/sdk";
import { client } from "@/sdk/client.gen";

const STORAGE_KEY = "eyrie.auth";

export type Scope = "user" | "account" | string;

export interface PersistedAuth {
  accessToken: string;
  refreshToken: string;
  /** ISO8601 instant when the access token expires (server-supplied). */
  expiresAt: string;
  /** Cached principal profile. Refreshed by /auth/me on boot. */
  principal: MeProfile | null;
  /** "user" (customer) or "account" (admin). */
  scope: Scope;
}

export interface AuthContextValue {
  /** The raw access token, or null if signed out. */
  token: string | null;
  /** The cached principal profile (user or account). */
  principal: MeProfile | null;
  /** "user" | "account" — whichever the JWT carries. */
  scope: Scope | null;
  /** True while restoring tokens / fetching /auth/me on boot. */
  loading: boolean;
  /** Customer login. */
  login: (creds: LoginInputBodyWritable) => Promise<void>;
  /** Admin (Account) login. */
  adminLogin: (creds: LoginInputBodyWritable) => Promise<void>;
  /** Best-effort server revoke + clear local state. */
  logout: () => Promise<void>;
  /** Force a refresh now. Returns the new access token. */
  refreshToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------- helpers ----------

interface JWTPayload {
  exp?: number; // seconds since epoch
  scope?: string;
  sub?: string;
  [k: string]: unknown;
}

function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as JWTPayload;
  } catch {
    return null;
  }
}

function readScope(token: string): Scope {
  const claims = decodeJWT(token);
  return (claims?.scope as Scope | undefined) ?? "user";
}

function loadPersisted(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAuth;
    if (!parsed.accessToken || !parsed.refreshToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(state: PersistedAuth) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function clearPersisted() {
  localStorage.removeItem(STORAGE_KEY);
}

function bundleToPersisted(
  bundle: TokenBundle,
  principal: MeProfile | null,
): PersistedAuth {
  return {
    accessToken: bundle.access_token,
    refreshToken: bundle.refresh_token,
    expiresAt: bundle.expires_at,
    principal,
    scope: principal?.scope ?? readScope(bundle.access_token),
  };
}

// ---------- provider ----------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PersistedAuth | null>(loadPersisted);
  const [loading, setLoading] = useState(true);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Wire the SDK request interceptor exactly once. It always reads from the
  // ref so token rotations propagate without re-registering the interceptor.
  useEffect(() => {
    const eject = client.interceptors.request.use((req) => {
      const cur = stateRef.current;
      if (cur?.accessToken) {
        req.headers.set("Authorization", `Bearer ${cur.accessToken}`);
      }
      return req;
    });
    return () => {
      client.interceptors.request.eject(eject);
    };
  }, []);

  const setSession = useCallback((next: PersistedAuth | null) => {
    if (next) persist(next);
    else clearPersisted();
    stateRef.current = next;
    setState(next);
  }, []);

  // Boot: if we have tokens, hydrate the principal from /auth/me.
  // Failures clear the session (treat as expired/invalid).
  useEffect(() => {
    let cancelled = false;
    const cur = stateRef.current;
    if (!cur) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data, error } = await authMe();
        if (cancelled) return;
        if (error || !data) {
          setSession(null);
        } else {
          setSession({ ...cur, principal: data, scope: data.scope ?? cur.scope });
        }
      } catch {
        if (!cancelled) setSession(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh helper, exposed and also used by the auto-refresh timer.
  const refreshToken = useCallback(async (): Promise<string | null> => {
    const cur = stateRef.current;
    if (!cur) return null;
    try {
      const { data, error } = await authRefresh({
        body: { refresh_token: cur.refreshToken },
      });
      if (error || !data) {
        setSession(null);
        return null;
      }
      const next: PersistedAuth = {
        ...cur,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        scope: cur.scope ?? readScope(data.access_token),
      };
      setSession(next);
      return next.accessToken;
    } catch {
      setSession(null);
      return null;
    }
  }, [setSession]);

  // Auto-refresh: schedule a refresh ~60s before expiry. The effect re-runs
  // every time the session changes (i.e. on each successful refresh).
  useEffect(() => {
    if (!state) return;
    const expiresAt = Date.parse(state.expiresAt);
    if (Number.isNaN(expiresAt)) return;
    const ms = Math.max(expiresAt - Date.now() - 60_000, 0);
    const t = window.setTimeout(() => {
      refreshToken();
    }, ms);
    return () => window.clearTimeout(t);
  }, [state, refreshToken]);

  const doLogin = useCallback(
    async (creds: LoginInputBodyWritable, isAdmin: boolean) => {
      const result = isAdmin
        ? await authAdminLogin({ body: creds })
        : await authLogin({ body: creds });
      const { data, error } = result;
      if (error || !data) {
        const detail =
          (error as { detail?: string } | undefined)?.detail ?? "Sign-in failed";
        throw new Error(detail);
      }
      // Stash tokens immediately so the follow-up authMe call carries the
      // freshly-issued bearer.
      const provisional = bundleToPersisted(data, null);
      setSession(provisional);
      try {
        const { data: meData } = await authMe();
        if (meData) {
          setSession({
            ...provisional,
            principal: meData,
            scope: meData.scope ?? provisional.scope,
          });
        }
      } catch {
        // Non-fatal — we still have tokens; principal will lazy-load later.
      }
    },
    [setSession],
  );

  const login = useCallback(
    (creds: LoginInputBodyWritable) => doLogin(creds, false),
    [doLogin],
  );
  const adminLogin = useCallback(
    (creds: LoginInputBodyWritable) => doLogin(creds, true),
    [doLogin],
  );

  const logout = useCallback(async () => {
    const cur = stateRef.current;
    if (cur) {
      try {
        await authLogout({ body: { refresh_token: cur.refreshToken } });
      } catch {
        // Best-effort.
      }
    }
    setSession(null);
  }, [setSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token: state?.accessToken ?? null,
      principal: state?.principal ?? null,
      scope: state?.scope ?? null,
      loading,
      login,
      adminLogin,
      logout,
      refreshToken,
    }),
    [state, loading, login, adminLogin, logout, refreshToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
